// src/views/editor.js

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    doc,
    getDoc,
    setDoc,
    serverTimestamp,
    collection,
    getDocs,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import "../css/App.css";

const STATUSES = [
    "Draft",
    "Ready for Deacons",
    "Ready to Publish",
    "Published",
];

export default function EditorPage({ currentUser, userRole }) {
    const { talkId } = useParams();
    const navigate = useNavigate();
    const [talk, setTalk] = useState({
        title: "",
        sections: [],
        status: "Draft",
        authorId: "",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);

    // Load from Firestore
    const loadTalk = useCallback(async () => {
        setLoading(true);
        if (!talkId) return setLoading(false);
        const snap = await getDoc(doc(db, "talks", talkId));
        if (snap.exists()) setTalk(snap.data());
        setLoading(false);
    }, [talkId]);

    useEffect(() => {
        loadTalk();
    }, [loadTalk]);

    // Load authors for dropdown (NATIONAL only)
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const snap = await getDocs(collection(db, "users"));

                const isChapterAffiliated = (data) => {
                    if (!data) return false;
                    const hasChapterId = typeof data.chapterId === "string" && data.chapterId.trim() !== "";
                    const hasSchoolId = typeof data.schoolId === "string" && data.schoolId.trim() !== "";
                    const hasChapter = typeof data.chapter === "string" && data.chapter.trim() !== "";
                    const hasSchool = typeof data.school === "string" && data.school.trim() !== "";
                    const hasChapters = Array.isArray(data.chapters) && data.chapters.length > 0;
                    const hasChapterIds = Array.isArray(data.chapterIds) && data.chapterIds.length > 0;
                    const hasAffiliations = Array.isArray(data.affiliations) && data.affiliations.length > 0;
                    const hasInvitedChapters = data.invitedChapters && typeof data.invitedChapters === "object" && Object.keys(data.invitedChapters).length > 0;
                    const hasChapterRoles = data.chapterRoles && typeof data.chapterRoles === "object" && Object.keys(data.chapterRoles).length > 0;
                    return (
                        hasChapterId ||
                        hasSchoolId ||
                        hasChapter ||
                        hasSchool ||
                        hasChapters ||
                        hasChapterIds ||
                        hasAffiliations ||
                        hasInvitedChapters ||
                        hasChapterRoles
                    );
                };

                const isExplicitNational = (data) => {
                    if (!data) return false;

                    // Primary distinction in our DB: national users have a national workflow role.
                    const role = String(data.role || "").toLowerCase().trim();
                    const isNationalRole = ["writer", "deacon", "publisher", "admin", "national_admin", "national"].includes(role);

                    // Backward-compatible flags (if present)
                    const hasNationalFlag = Boolean(
                        data.isNationalUser ||
                        data.isNational ||
                        data.nationalUser ||
                        data.national === true ||
                        data.scope === "national" ||
                        data.userType === "national" ||
                        data.level === "national"
                    );

                    return isNationalRole || hasNationalFlag;
                };

                const isExplicitChapterUser = (data) => {
                    if (!data) return false;
                    const role = String(data.role || "").toLowerCase().trim();
                    const isMemberRole = role === "member";
                    return Boolean(
                        isMemberRole ||
                        data.isChapterUser ||
                        data.chapterUser ||
                        data.scope === "chapter" ||
                        data.userType === "chapter" ||
                        data.level === "chapter"
                    );
                };

                const list = snap.docs
                    .map((d) => {
                        const data = d.data() || {};
                        const label = data.displayName || data.name || data.email || d.id;
                        return { id: d.id, label, data };
                    })
                    .filter((u) => {
                        // Exclude chapter-affiliated users
                        if (isChapterAffiliated(u.data) || isExplicitChapterUser(u.data)) return false;
                        // Only allow explicitly national users
                        return isExplicitNational(u.data);
                    })
                    .map(({ id, label }) => ({ id, label }));

                // De-dupe by label (defensive)
                const seenLabels = new Set();
                const deduped = [];
                for (const u of list) {
                    const key = String(u.label || "").trim().toLowerCase();
                    if (!key) continue;
                    if (seenLabels.has(key)) continue;
                    seenLabels.add(key);
                    deduped.push(u);
                }

                deduped.sort((a, b) => a.label.localeCompare(b.label));
                setUsers(deduped);
            } catch (e) {
                console.error("Error loading users:", e);
                setUsers([]);
            } finally {
                setUsersLoading(false);
            }
        };
        fetchUsers();
    }, []);

    // Save content + status
    const saveTalk = useCallback(async (newStatus = talk.status) => {
        setSaving(true);

        // Determine Firestore reference: existing talk or new talk
        let talkRef;
        if (talkId) {
            talkRef = doc(db, "talks", talkId);
        } else {
            talkRef = doc(collection(db, "talks")); // auto-generated ID
        }

        // Save or merge the talk data
        await setDoc(
            talkRef,
            { ...talk, status: newStatus, updatedAt: serverTimestamp() },
            { merge: true }
        );

        // If this was a new talk, navigate to its URL
        if (!talkId) {
            navigate(`/editor/${talkRef.id}`, { replace: true });
        }

        // Update local state
        setTalk((t) => ({ ...t, status: newStatus }));
        setSaving(false);
    }, [talk, talkId, navigate]);

    // Keyboard shortcut: Cmd/Ctrl + S to save
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Use lower-case check to be robust across browsers
            if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 's') {
                e.preventDefault();
                if (!saving) {
                    saveTalk();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [saveTalk, saving]);

    // Kick off Cloud Functions
    const generateAsset = async (type) => {
        try {
            const response = await fetch(
                `https://us-central1-sole-15b6f.cloudfunctions.net/generate${type}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ talkId }),
                }
            );
            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Error generating ${type}:`, errorText);
                alert(`Failed to start ${type} generation: ${errorText}`);
                return;
            }
            alert(`${type} generation started—check back soon!`);
        } catch (err) {
            console.error(`Network error generating ${type}:`, err);
            alert(`Network error: could not start ${type} generation. Please try again later.`);
        }
    };

    if (loading) return <p>Loading talk…</p>;

    return (
        <>
            <Navbar />
            <main className="editor-page">
                <div className="editor-hero" style={{ maxWidth: 720, margin: "0 auto" }}>
                    <h1>{talk.title || "New Talk"}</h1>
                    <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: 600 }}>Title</label>
                    <input
                        type="text"
                        placeholder="Enter a title…"
                        value={talk.title}
                        onChange={(e) => setTalk((t) => ({ ...t, title: e.target.value }))}
                        className="subscribe-input"
                        style={{ width: "100%", marginBottom: "1rem" }}
                    />

                    <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: 600 }}>Author</label>
                    <select
                        value={talk.authorId || ""}
                        onChange={(e) => setTalk((t) => ({ ...t, authorId: e.target.value }))}
                        disabled={usersLoading}
                        className="status-selector"
                        style={{ width: "100%", marginBottom: "1.25rem" }}
                    >
                        <option value="">— Select Author —</option>
                        {users.map((u) => (
                            <option key={u.id} value={u.id}>{u.label}</option>
                        ))}
                    </select>

                    <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button className="slideshow-button" onClick={() => saveTalk()} disabled={saving}>
                            Save
                        </button>
                        <button className="slideshow-button" type="button" onClick={() => navigate("/national")}>Cancel</button>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
}